"use client"

import { useState, useEffect, useRef, useActionState, useTransition, useMemo } from "react"
import { useTranslations } from "next-intl"
import { AlertCircleIcon, CheckCircle2Icon } from "lucide-react"
import type { ZodType } from "zod"
import { Button } from "@/components/ui/button"
import { InlineNotification } from "@/components/ui/InlineNotification"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

/**
 * Shared submission-state shape for the booking form. A caller's Server Action
 * must resolve to this (the naturstore `BookingActionState` is structurally
 * identical, so it satisfies this contract without any adapter).
 */
export type BookingFormState =
  | { success: true; error: null; fieldErrors: null; submittedAt: number }
  | {
      success: false
      error: string | null
      fieldErrors: Record<string, string[]> | null
      submittedAt: number
    }

export type BookingAction = (
  prevState: BookingFormState,
  formData: FormData,
) => Promise<BookingFormState>

export interface BookingFormProps {
  /**
   * Server Action invoked with the collected FormData. Passed in by the caller so
   * this base form stays decoupled from any brand-/route-specific action.
   */
  action: BookingAction
  /**
   * Zod schema used for the client-side validation gate. Its issue messages MUST
   * be i18n keys (E3) resolvable in the caller's `booking` namespace. Keyed by the
   * form field names (name, email, phone, preferredDate, preferredTimeOfDay,
   * alternativeDate, notes, consent).
   */
  schema: ZodType<unknown>
  /** Max length for the notes field; drives the live character counter. */
  notesMaxLength?: number
}

const initialState: BookingFormState = {
  success: false,
  error: null,
  fieldErrors: null,
  submittedAt: 0,
}

const TIME_OF_DAY_OPTIONS = ["morning", "afternoon", "evening"] as const

// E3: top-level error keys the form is willing to render. Anything outside this
// set falls back to "bookingSubmitError" so we never feed a raw API string to
// next-intl (which would surface MISSING_MESSAGE in the UI).
const ALLOWED_ERROR_KEYS = ["bookingSubmitError", "rateLimitExceeded"] as const

function isAllowedErrorKey(value: string): boolean {
  return (ALLOWED_ERROR_KEYS as readonly string[]).includes(value)
}

/** Tomorrow's date as YYYY-MM-DD — the earliest selectable booking date. */
function tomorrowISO(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Neutral base booking / appointment-request form.
 *
 * Brand-agnostic: the Server Action and validation schema arrive as props and all
 * copy comes from the caller's `booking` i18n namespace, so a theme can reuse it
 * unchanged or override it at the registry key `booking/BookingForm`.
 *
 * Preserved engineering (harvested from naturstore):
 *  - honeypot `website` field (anti-spam), gated client-side before dispatch,
 *  - React-19 useActionState + onSubmit + startTransition (E2 form-reset-safe),
 *  - client-side schema gate so invalid submits don't burn rate-limit budget,
 *  - per-field error focus on validation failure (FM7),
 *  - aria-invalid / aria-describedby per field, role="alert" live region,
 *  - live character counter on the notes field,
 *  - min-date guard (tomorrow onwards),
 *  - i18n-key-based error mapping (E3).
 */
export function BookingForm({ action, schema, notesMaxLength = 1000 }: BookingFormProps) {
  const t = useTranslations("booking")
  const [isPending, startTransition] = useTransition()
  const [formState, formAction] = useActionState(action, initialState)

  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [preferredDate, setPreferredDate] = useState("")
  const [preferredTimeOfDay, setPreferredTimeOfDay] =
    useState<(typeof TIME_OF_DAY_OPTIONS)[number]>("morning")
  const [alternativeDate, setAlternativeDate] = useState("")
  const [notes, setNotes] = useState("")
  const [consent, setConsent] = useState(false)

  // Client-side validation errors — gate the Server Action so invalid submits
  // don't burn rate-limit budget. Schema messages are already i18n keys (E3).
  const [clientErrors, setClientErrors] = useState<Record<string, string[]> | null>(null)

  // Client errors take precedence over the last server response.
  const fieldErrors = clientErrors ?? formState.fieldErrors

  const minDate = useMemo(() => tomorrowISO(), [])

  const nameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const preferredDateRef = useRef<HTMLInputElement>(null)
  const alternativeDateRef = useRef<HTMLInputElement>(null)
  const notesRef = useRef<HTMLTextAreaElement>(null)
  const consentRef = useRef<HTMLInputElement>(null)

  // Success: clear all fields. The confirmation renders inline (storefront
  // convention) and persists until the user acts or navigates away. Depend on
  // submittedAt so consecutive identical successes re-trigger the field clear.
  useEffect(() => {
    if (formState.submittedAt > 0 && formState.success) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear controlled fields once the server confirms a successful submission
      setName("")
      setEmail("")
      setPhone("")
      setPreferredDate("")
      setPreferredTimeOfDay("morning")
      setAlternativeDate("")
      setNotes("")
      setConsent(false)
      setClientErrors(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.submittedAt])

  // Focus the first field with an error on validation failure (FM7).
  useEffect(() => {
    if (!formState.success && fieldErrors) {
      if (fieldErrors.name) nameRef.current?.focus()
      else if (fieldErrors.email) emailRef.current?.focus()
      else if (fieldErrors.phone) phoneRef.current?.focus()
      else if (fieldErrors.preferredDate) preferredDateRef.current?.focus()
      else if (fieldErrors.alternativeDate) alternativeDateRef.current?.focus()
      else if (fieldErrors.notes) notesRef.current?.focus()
      else if (fieldErrors.consent) consentRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldErrors])

  const showSuccess = formState.success
  const errorKey =
    !clientErrors && !formState.success && formState.error
      ? isAllowedErrorKey(formState.error)
        ? formState.error
        : "bookingSubmitError"
      : null

  // E2: onSubmit + e.preventDefault() + startTransition(() => formAction(fd)).
  // NEVER use action={formAction} — React 19 auto-resets all fields on completion.
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget

    // Honeypot gate — a filled `website` field means a bot. Silently drop the
    // submission client-side so the action (and rate-limit budget) is never touched.
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website"]')
    if (honeypotEl?.value) {
      return
    }

    // Client-side gate — schema returns translation-key error messages (E3).
    const parsed = schema.safeParse({
      name,
      email,
      phone,
      preferredDate,
      preferredTimeOfDay,
      alternativeDate,
      notes,
      consent: consent ? "on" : "",
    })
    if (!parsed.success) {
      const errs: Record<string, string[]> = {}
      const fieldErrs = parsed.error.flatten().fieldErrors as Record<string, string[] | undefined>
      for (const [k, v] of Object.entries(fieldErrs)) {
        if (v && v.length > 0) errs[k] = v
      }
      setClientErrors(errs)
      return
    }
    setClientErrors(null)

    const fd = new FormData()
    fd.set("name", name)
    fd.set("email", email)
    fd.set("phone", phone)
    fd.set("preferredDate", preferredDate)
    fd.set("preferredTimeOfDay", preferredTimeOfDay)
    fd.set("alternativeDate", alternativeDate)
    fd.set("notes", notes)
    fd.set("consent", consent ? "on" : "")
    fd.set("website", honeypotEl?.value ?? "")

    startTransition(() => {
      formAction(fd)
    })
  }

  return (
    <form data-testid="booking-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
      {/* Submission outcome — inline, not a floating toast. */}
      {showSuccess && (
        <div data-testid="booking-success">
          <InlineNotification variant="success" icon={<CheckCircle2Icon />}>
            {t("bookingSuccess")}
          </InlineNotification>
        </div>
      )}

      {errorKey && (
        <div data-testid="booking-error">
          <InlineNotification variant="error" icon={<AlertCircleIcon />}>
            {t(errorKey as Parameters<typeof t>[0])}
          </InlineNotification>
        </div>
      )}

      {/* Honeypot field — hidden from real users, filled by bots. */}
      <input
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      {/* Aria-live region for screen readers — rendered always, content conditional. */}
      <div role="alert" aria-live="polite" className="sr-only">
        {fieldErrors && t("validationErrors")}
      </div>

      {/* Name */}
      <div className="space-y-1">
        <Label htmlFor="booking-name" required>
          {t("nameLabel")}
        </Label>
        <Input
          ref={nameRef}
          id="booking-name"
          name="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isPending}
          autoComplete="name"
          required
          aria-invalid={fieldErrors?.name ? "true" : undefined}
          aria-describedby={fieldErrors?.name ? "booking-name-error" : undefined}
          placeholder={t("namePlaceholder")}
        />
        {fieldErrors?.name && (
          <p
            id="booking-name-error"
            data-testid="booking-name-error"
            role="alert"
            className="text-sm text-destructive"
          >
            {t(fieldErrors.name[0] as Parameters<typeof t>[0])}
          </p>
        )}
      </div>

      {/* Email */}
      <div className="space-y-1">
        <Label htmlFor="booking-email" required>
          {t("emailLabel")}
        </Label>
        <Input
          ref={emailRef}
          id="booking-email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isPending}
          autoComplete="email"
          required
          aria-invalid={fieldErrors?.email ? "true" : undefined}
          aria-describedby={fieldErrors?.email ? "booking-email-error" : undefined}
          placeholder={t("emailPlaceholder")}
        />
        {fieldErrors?.email && (
          <p
            id="booking-email-error"
            data-testid="booking-email-error"
            role="alert"
            className="text-sm text-destructive"
          >
            {t(fieldErrors.email[0] as Parameters<typeof t>[0])}
          </p>
        )}
      </div>

      {/* Phone (optional) */}
      <div className="space-y-1">
        <Label htmlFor="booking-phone">
          {t("phoneLabel")}{" "}
          <span className="text-muted-foreground font-normal text-xs">({t("phoneOptional")})</span>
        </Label>
        <Input
          ref={phoneRef}
          id="booking-phone"
          name="phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={isPending}
          autoComplete="tel"
          aria-invalid={fieldErrors?.phone ? "true" : undefined}
          aria-describedby={fieldErrors?.phone ? "booking-phone-error" : undefined}
          placeholder={t("phonePlaceholder")}
        />
        {fieldErrors?.phone && (
          <p
            id="booking-phone-error"
            data-testid="booking-phone-error"
            role="alert"
            className="text-sm text-destructive"
          >
            {t(fieldErrors.phone[0] as Parameters<typeof t>[0])}
          </p>
        )}
      </div>

      {/* Preferred date + alternative date */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="booking-preferred-date" required>
            {t("preferredDateLabel")}
          </Label>
          <Input
            ref={preferredDateRef}
            id="booking-preferred-date"
            name="preferredDate"
            type="date"
            min={minDate}
            value={preferredDate}
            onChange={(e) => setPreferredDate(e.target.value)}
            disabled={isPending}
            required
            aria-invalid={fieldErrors?.preferredDate ? "true" : undefined}
            aria-describedby={
              fieldErrors?.preferredDate ? "booking-preferred-date-error" : undefined
            }
          />
          {fieldErrors?.preferredDate && (
            <p
              id="booking-preferred-date-error"
              data-testid="booking-preferred-date-error"
              role="alert"
              className="text-sm text-destructive"
            >
              {t(fieldErrors.preferredDate[0] as Parameters<typeof t>[0])}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="booking-alternative-date">
            {t("alternativeDateLabel")}{" "}
            <span className="text-muted-foreground font-normal text-xs">
              ({t("phoneOptional")})
            </span>
          </Label>
          <Input
            ref={alternativeDateRef}
            id="booking-alternative-date"
            name="alternativeDate"
            type="date"
            min={minDate}
            value={alternativeDate}
            onChange={(e) => setAlternativeDate(e.target.value)}
            disabled={isPending}
            aria-invalid={fieldErrors?.alternativeDate ? "true" : undefined}
            aria-describedby={
              fieldErrors?.alternativeDate ? "booking-alternative-date-error" : undefined
            }
          />
          {fieldErrors?.alternativeDate && (
            <p
              id="booking-alternative-date-error"
              data-testid="booking-alternative-date-error"
              role="alert"
              className="text-sm text-destructive"
            >
              {t(fieldErrors.alternativeDate[0] as Parameters<typeof t>[0])}
            </p>
          )}
        </div>
      </div>

      {/* Time of day */}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium leading-none">
          {t("timeOfDayLabel")}
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        </legend>
        <div className="flex flex-wrap gap-3 pt-1">
          {TIME_OF_DAY_OPTIONS.map((opt) => {
            const id = `booking-time-${opt}`
            return (
              <label
                key={opt}
                htmlFor={id}
                className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2 text-sm transition-colors has-[:checked]:border-foreground has-[:checked]:bg-foreground has-[:checked]:text-background"
              >
                <input
                  id={id}
                  type="radio"
                  name="preferredTimeOfDay"
                  value={opt}
                  checked={preferredTimeOfDay === opt}
                  onChange={() => setPreferredTimeOfDay(opt)}
                  disabled={isPending}
                  className="sr-only"
                />
                {t(`timeOfDay.${opt}` as Parameters<typeof t>[0])}
              </label>
            )
          })}
        </div>
        {fieldErrors?.preferredTimeOfDay && (
          <p
            data-testid="booking-time-of-day-error"
            role="alert"
            className="text-sm text-destructive"
          >
            {t(fieldErrors.preferredTimeOfDay[0] as Parameters<typeof t>[0])}
          </p>
        )}
      </fieldset>

      {/* Notes */}
      <div className="space-y-1">
        <Label htmlFor="booking-notes" required>
          {t("notesLabel")}
        </Label>
        <Textarea
          ref={notesRef}
          id="booking-notes"
          name="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isPending}
          rows={5}
          maxLength={notesMaxLength}
          required
          aria-invalid={fieldErrors?.notes ? "true" : undefined}
          aria-describedby={fieldErrors?.notes ? "booking-notes-error" : undefined}
          placeholder={t("notesPlaceholder")}
        />
        <p className="text-xs text-muted-foreground text-right">
          {notes.trim().length}/{notesMaxLength}
        </p>
        {fieldErrors?.notes && (
          <p
            id="booking-notes-error"
            data-testid="booking-notes-error"
            role="alert"
            className="text-sm text-destructive"
          >
            {t(fieldErrors.notes[0] as Parameters<typeof t>[0])}
          </p>
        )}
      </div>

      {/* Consent */}
      <div className="space-y-1">
        <label htmlFor="booking-consent" className="flex items-start gap-3 text-sm">
          <input
            ref={consentRef}
            id="booking-consent"
            name="consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            disabled={isPending}
            required
            aria-invalid={fieldErrors?.consent ? "true" : undefined}
            aria-describedby={fieldErrors?.consent ? "booking-consent-error" : undefined}
            className="mt-0.5 size-4 cursor-pointer"
          />
          <span className="text-muted-foreground leading-relaxed">{t("consentLabel")}</span>
        </label>
        {fieldErrors?.consent && (
          <p
            id="booking-consent-error"
            data-testid="booking-consent-error"
            role="alert"
            className="text-sm text-destructive"
          >
            {t(fieldErrors.consent[0] as Parameters<typeof t>[0])}
          </p>
        )}
      </div>

      <Button type="submit" disabled={isPending}>
        {isPending ? t("submitting") : t("submit")}
      </Button>
    </form>
  )
}

export default BookingForm
